import React, { useEffect, useMemo, useState, useRef } from "react";
import { toast } from "sonner";
import {
  DollarSign, Car, Calendar, TrendingUp, Clock, AlertTriangle, Sparkles,
  Wallet, Loader2, Search, ArrowUpRight, ArrowDownRight, Phone, Mail, MapPin,
  RefreshCw, Bell, Zap, ShieldAlert, Crown, ChevronRight, FileText,
  PlayCircle, PauseCircle, XCircle, CheckCircle2, ExternalLink,
} from "lucide-react";
import { api } from "../../lib/api";

/**
 * CommandCenterTab — Plenty Rydez COMMAND CENTER.
 *
 * Single operational screen for management. All data comes from
 * GET /admin/command-center (aggregated) + GET /admin/command-center/fleet-status.
 * Auto-refresh every 30 s. Uses existing bookings / vehicles / crm_leads /
 * blacklists — no new customer or vehicle databases.
 *
 * Phase 1 (this session): KPIs, Today's Pickups, Today's Returns,
 * Late Returns, Money Due, Live Pipeline, Follow-Ups, Fleet Status grid,
 * Alerts, Activity Feed, Global Search, Quick Actions.
 *
 * Phase 2 (next turns): Incident Center, Chauffeur Dispatch, Fleet Attention,
 * Manager Task Center, Audit Log, End-of-Day PDF summary.
 */
export default function CommandCenterTab() {
  const [data, setData] = useState(null);
  const [fleet, setFleet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tick, setTick] = useState(0);   // repaint for live clock
  const [drill, setDrill] = useState(null);
  const refreshTimer = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [cc, fl] = await Promise.all([
        api.get("/admin/command-center"),
        api.get("/admin/command-center/fleet-status"),
      ]);
      setData(cc.data);
      setFleet(fl.data || []);
    } catch (err) {
      if (!silent) toast.error("Failed to load Command Center");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    refreshTimer.current = setInterval(() => load(true), 30000);
    const clock = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(refreshTimer.current); clearInterval(clock); };
  }, []);

  const now = new Date();
  const search = q.trim().toLowerCase();

  const globalMatches = useMemo(() => {
    if (!search || !data) return [];
    const hits = [];
    const push = (kind, label, meta, action) => hits.push({ kind, label, meta, action });
    (data.pickups_today || []).forEach((b) => {
      if ([b.full_name, b.customer_email, b.customer_phone, b.vehicle_name, b.id]
          .some((f) => (f || "").toLowerCase().includes(search))) {
        push("Today pickup", b.full_name, `${b.vehicle_name} · $${b.amount || 0}`);
      }
    });
    (data.late_rentals || []).forEach((b) => {
      if ([b.full_name, b.vehicle_name].some((f) => (f || "").toLowerCase().includes(search))) {
        push("Late rental", b.full_name, `${b.vehicle_name} · ${b.hours_late}h overdue`);
      }
    });
    (data.hot_leads || []).forEach((l) => {
      if ([l.name, l.email, l.phone, l.vehicle_name].some((f) => (f || "").toLowerCase().includes(search))) {
        push("Hot lead", l.name, `${l.source} · $${l.estimated_value || 0}`);
      }
    });
    fleet.forEach((v) => {
      if ([v.name, v.license_plate, v.model].some((f) => (f || "").toLowerCase().includes(search))) {
        push("Vehicle", v.name, `${v.status} · ${v.license_plate || "no plate"}`);
      }
    });
    return hits.slice(0, 15);
  }, [q, data, fleet]);

  if (loading || !data) {
    return (
      <div className="p-16 text-center text-white/60 inline-flex items-center gap-2 justify-center w-full">
        <Loader2 size={16} className="animate-spin text-cyan" /> Booting the Command Center…
      </div>
    );
  }

  const K = data.kpis;
  const revenueTodayDelta = deltaPct(K.revenue_today, K.revenue_yesterday);
  const revenueWeekDelta = deltaPct(K.revenue_this_week, K.revenue_last_week);
  const revenueMonthDelta = deltaPct(K.revenue_this_month, K.revenue_last_month);

  // Build drilldown rows for each KPI from the data we already loaded so a
  // click reveals the underlying records without a second round-trip.
  const chauffeurToday = (data.pickups_today || []).filter((b) => b.type === "ride");
  const rentalsInProgress = fleet.filter((v) => ["OUT_ON_RENTAL", "LATE"].includes(v.status));
  const upcoming = data.pickups_today || [];
  const depositsRows = (data.money_due || []).filter((b) => b.deposit_status === "pending");
  const outstandingRows = (data.money_due || []).filter((b) => ["pending", "unpaid"].includes(b.payment_status));

  const openDrill = (key, title, rows, render) => setDrill({ key, title, rows, render });

  return (
    <div data-testid="command-center" className="space-y-5">
      {/* Header */}
      <div className="rounded-3xl border border-cyan/25 bg-gradient-to-br from-cyan/[0.08] via-cyan/[0.02] to-transparent p-6 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-cyan/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="label-caps text-cyan text-[10px] tracking-[0.35em]">Plenty Rydez</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-white mt-1 leading-none">
              Command Center
            </h2>
            <div className="text-white/60 text-xs mt-2 flex flex-wrap items-center gap-4">
              <span>{now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</span>
              <span className="text-cyan tabular-nums">{now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
              <span className="label-caps text-[9px] text-white/50">
                {data.employee?.name} · {data.employee?.role || "staff"}
              </span>
              <button
                data-testid="cc-refresh"
                onClick={() => load()}
                className="inline-flex items-center gap-1 label-caps text-[9px] text-cyan/80 hover:text-cyan transition"
              >
                <RefreshCw size={10} /> Auto-refresh 30s
              </button>
            </div>
          </div>

          {/* Global search */}
          <div className="relative w-full sm:w-96">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="cc-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer · plate · booking · vehicle · phone…"
              className="w-full pl-9 pr-3 py-2.5 bg-black/40 border border-white/10 focus:border-cyan/60 rounded-full text-sm text-white outline-none"
            />
            {search && (
              <div className="absolute top-full left-0 right-0 mt-1 z-30 rounded-xl border border-white/10 bg-ink-900 shadow-xl max-h-72 overflow-auto">
                {globalMatches.length === 0 ? (
                  <div className="p-3 text-white/50 text-xs italic">No matches.</div>
                ) : globalMatches.map((m, i) => (
                  <div key={i} className="px-3 py-2 border-b border-white/5 last:border-0 text-xs">
                    <div className="label-caps text-[9px] text-cyan mb-0.5">{m.kind}</div>
                    <div className="text-white truncate">{m.label}</div>
                    <div className="text-white/40 text-[11px] truncate">{m.meta}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Critical Alerts */}
      {data.alerts?.length > 0 && (
        <div data-testid="cc-alerts" className="rounded-2xl border border-red-400/40 bg-red-500/5 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Bell size={14} className="text-red-300" />
            <div className="label-caps text-[10px] text-red-200 tracking-widest">
              Smart alerts · {data.alerts.length}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {data.alerts.slice(0, 6).map((a, i) => (
              <div key={i} className="rounded-lg border border-red-400/25 bg-black/30 p-2.5 text-xs flex items-start gap-2">
                <ShieldAlert size={12} className={
                  a.level === "urgent" ? "text-red-300" : "text-amber-300"
                } />
                <div className="min-w-0 flex-1">
                  <div className="text-white truncate">{a.label}</div>
                  {a.meta && <div className="text-white/40 text-[10px] truncate">{a.meta}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        <KpiCard label="Revenue · Today"  value={`$${K.revenue_today.toLocaleString()}`} delta={revenueTodayDelta} icon={DollarSign} tone="lime"
          onClick={() => openDrill("rev_today", "Bookings created today", data.pickups_today, renderBookingRow)} />
        <KpiCard label="Revenue · Week"   value={`$${K.revenue_this_week.toLocaleString()}`} delta={revenueWeekDelta} icon={TrendingUp} tone="lime"
          onClick={() => setDrill({ key: "rev_week", title: `Revenue this week: $${K.revenue_this_week.toLocaleString()}`,
            rows: [{ note: "Weekly revenue rollup — drill in to Analytics tab for line-by-line breakdown." }],
            render: (r) => <div className="p-4 text-white/70 text-sm">{r.note}</div> })} />
        <KpiCard label="Revenue · Month"  value={`$${K.revenue_this_month.toLocaleString()}`} delta={revenueMonthDelta} icon={TrendingUp} tone="lime"
          onClick={() => setDrill({ key: "rev_month", title: `Revenue this month: $${K.revenue_this_month.toLocaleString()}`,
            rows: [{ note: "Monthly revenue rollup — full report available in the Analytics tab." }],
            render: (r) => <div className="p-4 text-white/70 text-sm">{r.note}</div> })} />
        <KpiCard label="Active Rentals"   value={K.active_rentals}    icon={Car}       tone="cyan"
          onClick={() => openDrill("active", "Active rentals", rentalsInProgress, renderVehicleRow)} />
        <KpiCard label="Vehicles Out"     value={K.active_rentals}    icon={Car}       tone="amber"
          onClick={() => openDrill("out", "Vehicles currently out", rentalsInProgress, renderVehicleRow)} />
        <KpiCard label="Returning Today"  value={K.returning_today}   icon={Calendar}  tone="cyan"
          onClick={() => openDrill("returns", "Returning today", data.returns_today, renderBookingRow)} />
        <KpiCard label="Upcoming Pickups" value={K.upcoming_pickups}  icon={Calendar}  tone="cyan"
          onClick={() => openDrill("pickups", "Upcoming pickups", upcoming, renderBookingRow)} />
        <KpiCard label="Chauffeur Today"  value={K.chauffeur_today}   icon={Car}       tone="cyan"
          onClick={() => openDrill("chauffeur", "Chauffeur jobs today", chauffeurToday, renderBookingRow)} />
        <KpiCard label="Deposits Pending" value={K.deposits_pending}  icon={Wallet}    tone="amber"
          onClick={() => openDrill("dep", "Deposits pending", depositsRows, renderMoneyRow)} />
        <KpiCard label="Outstanding"      value={K.outstanding_balances} icon={DollarSign} tone="red"
          onClick={() => openDrill("out_bal", "Outstanding balances", outstandingRows, renderMoneyRow)} />
        <KpiCard label="New Leads"        value={K.new_leads}         icon={Sparkles}  tone="cyan"
          onClick={() => { window.location.hash = "#crm"; toast.info("Opening leads queue…"); }} />
        <KpiCard label="Follow-ups Due"   value={K.follow_ups_due}    icon={Clock}     tone="amber"
          onClick={() => openDrill("fu", "Follow-ups due", data.follow_ups_due, renderFollowUpRow)} />
        <KpiCard label="Late / Overdue"   value={K.late_count}        icon={AlertTriangle} tone="red"
          onClick={() => openDrill("late", "Late / overdue rentals", data.late_rentals, renderBookingRow)} />
        <KpiCard label="Fleet Size"       value={K.fleet_size}        icon={Car}       tone="white"
          onClick={() => openDrill("fleet", "Every vehicle in your fleet", fleet, renderVehicleRow)} />
      </div>

      {/* Quick actions */}
      <QuickActions />

      {drill && <DrilldownModal drill={drill} onClose={() => setDrill(null)} />}

      {/* Main grid */}
      <div className="grid xl:grid-cols-3 gap-4">
        {/* Column 1 — Pickups + Returns */}
        <div className="space-y-4">
          <Panel title="Today's Pickups" count={data.pickups_today?.length} accent="cyan" testId="cc-pickups">
            {data.pickups_today?.length === 0 && <Empty label="No pickups scheduled today" />}
            {data.pickups_today?.map((b) => (
              <BookingRow key={b.id} b={b} kind="pickup" />
            ))}
          </Panel>

          <Panel title="Today's Returns" count={data.returns_today?.length} accent="cyan" testId="cc-returns">
            {data.returns_today?.length === 0 && <Empty label="No returns due today" />}
            {data.returns_today?.map((b) => (
              <BookingRow key={b.id} b={b} kind="return" />
            ))}
          </Panel>
        </div>

        {/* Column 2 — Late + Money Due */}
        <div className="space-y-4">
          <Panel title="Late / Overdue" count={data.late_rentals?.length} accent="red" testId="cc-late">
            {data.late_rentals?.length === 0 && <Empty label="No overdue vehicles — 🎉" />}
            {data.late_rentals?.map((b) => (
              <BookingRow key={b.id} b={b} kind="late" />
            ))}
          </Panel>

          <Panel title="Money Due" count={data.money_due?.length} accent="amber" testId="cc-money"
                 subtitle={`Total flagged: $${(data.money_due || []).reduce((s, b) => s + (b.amount || 0), 0).toLocaleString()}`}>
            {data.money_due?.length === 0 && <Empty label="No outstanding balances" />}
            {data.money_due?.map((b) => (
              <div key={b.id} className="p-3 border-b border-white/5 last:border-0 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 border border-amber-400/40 flex items-center justify-center shrink-0">
                  <DollarSign size={13} className="text-amber-200" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white text-sm font-medium truncate">{b.full_name}</div>
                  <div className="text-white/50 text-[11px] truncate">{b.vehicle_name}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-amber-200 font-medium text-sm">${(b.amount || 0).toFixed(0)}</div>
                  <div className="label-caps text-[9px] text-white/40">{b.deposit_status || b.payment_status}</div>
                </div>
              </div>
            ))}
          </Panel>
        </div>

        {/* Column 3 — Pipeline + Hot leads + Follow-ups + Feed */}
        <div className="space-y-4">
          <Panel title="Live Sales Pipeline" accent="cyan" testId="cc-pipeline">
            <div className="grid grid-cols-2 gap-1 p-2">
              {[
                ["new_lead", "New Lead"], ["contacted", "Contacted"],
                ["quote_sent", "Quote Sent"], ["follow_up_needed", "Follow-Up"],
                ["deposit_pending", "Deposit"], ["booked", "Booked"],
                ["lost_lead", "Lost"], ["vip_customer", "VIP"],
              ].map(([k, l]) => (
                <div key={k} className="flex items-center justify-between rounded-lg bg-white/5 px-2.5 py-1.5">
                  <span className="text-white/70 text-[11px]">{l}</span>
                  <span className="text-cyan font-serif text-lg tabular-nums">{data.pipeline_counts?.[k] || 0}</span>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Hot Leads" count={data.hot_leads?.length} accent="amber" testId="cc-hot">
            {data.hot_leads?.length === 0 && <Empty label="No hot leads right now" />}
            {data.hot_leads?.map((l) => (
              <div key={l.id} className="p-3 border-b border-white/5 last:border-0 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-400/20 border border-amber-300/40 flex items-center justify-center shrink-0">
                  <Zap size={13} className="text-amber-200" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white text-sm font-medium truncate">{l.name}</div>
                  <div className="text-white/50 text-[11px] truncate">
                    {l.vehicle_name || l.service} · {l.source} · {(l.stage || "").replace(/_/g, " ")}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-lime-300 font-medium text-sm">${(l.estimated_value || 0).toFixed(0)}</div>
                </div>
              </div>
            ))}
          </Panel>

          <Panel title="Follow-Ups Due" count={data.follow_ups_due?.length} accent="amber" testId="cc-followups">
            {data.follow_ups_due?.length === 0 && <Empty label="All follow-ups clear ✨" />}
            {data.follow_ups_due?.map((f) => (
              <div key={f.id} className="p-3 border-b border-white/5 last:border-0">
                <div className="text-white text-sm">{f.name} · <span className="text-white/50 text-xs">{f.vehicle_name || f.service}</span></div>
                <div className="text-amber-200 text-[11px] mt-0.5">
                  {f.next_follow_up ? `Due ${new Date(f.next_follow_up).toLocaleString()}` : "No date set"}
                </div>
              </div>
            ))}
          </Panel>

          <Panel title="Live Activity" count={data.activity_feed?.length} accent="cyan" testId="cc-feed">
            <div className="max-h-80 overflow-y-auto">
              {data.activity_feed?.map((e, i) => (
                <div key={i} className="p-2.5 border-b border-white/5 last:border-0 flex items-start gap-2 text-xs">
                  <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
                    e.kind === "booking" ? "bg-cyan"
                    : e.kind === "lead" ? "bg-amber-300"
                    : "bg-white/40"
                  }`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-white truncate">{e.label}</div>
                    <div className="text-white/40 text-[10px] truncate">{e.meta}</div>
                    <div className="text-white/30 text-[10px]">{e.at ? new Date(e.at).toLocaleString() : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>

      {/* Fleet Status Grid */}
      <FleetStatusGrid fleet={fleet} />
    </div>
  );
}

/* -------- KPI + Panel primitives -------- */

function KpiCard({ label, value, delta, icon: Icon, tone = "cyan", onClick }) {
  const tones = {
    cyan:  "text-cyan border-cyan/25 bg-cyan/[0.04]",
    amber: "text-amber-300 border-amber-400/25 bg-amber-500/[0.04]",
    lime:  "text-lime-300 border-lime-400/25 bg-lime-500/[0.04]",
    red:   "text-red-300 border-red-400/25 bg-red-500/[0.04]",
    white: "text-white/80 border-white/15 bg-white/[0.03]",
  };
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      data-testid={`cc-kpi-${(label || "").toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
      onClick={onClick}
      className={`rounded-xl border p-3 text-left transition ${tones[tone]} ${onClick ? "hover:brightness-125 hover:border-current cursor-pointer" : ""}`}
    >
      <div className="flex items-center justify-between">
        <div className="label-caps text-[8px] text-white/60 tracking-widest truncate">{label}</div>
        <Icon size={11} className="opacity-70" />
      </div>
      <div className="font-serif text-2xl mt-1 tabular-nums">{value}</div>
      {delta != null && (
        <div className={`inline-flex items-center gap-0.5 text-[10px] mt-1 ${delta >= 0 ? "text-lime-300" : "text-red-300"}`}>
          {delta >= 0 ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
          {Math.abs(delta).toFixed(0)}%
        </div>
      )}
    </Comp>
  );
}

/* -------- Drilldown modal + row renderers -------- */

function DrilldownModal({ drill, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 flex items-start justify-center overflow-y-auto"
      onClick={onClose}>
      <div className="bg-ink-900 border border-cyan/25 rounded-2xl max-w-2xl w-full my-8"
        data-testid="cc-drilldown" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="label-caps text-cyan text-[10px] tracking-widest">Command Center · drilldown</div>
            <h3 className="font-serif text-xl text-white mt-0.5">{drill.title}</h3>
            <div className="text-white/40 text-xs mt-0.5">
              {drill.rows?.length || 0} record{(drill.rows?.length || 0) === 1 ? "" : "s"}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5"><XCircle size={16} /></button>
        </div>
        <div className="divide-y divide-white/5 max-h-[70vh] overflow-auto">
          {(drill.rows || []).length === 0 ? (
            <div className="p-10 text-center text-white/40 text-sm italic">Nothing to show here yet.</div>
          ) : (drill.rows || []).map((r, i) => (
            <div key={r.id || i}>{drill.render(r, i)}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function renderBookingRow(b) {
  const time = b.pickup_datetime || b.start_at || b.end_at;
  return (
    <div className="p-3 flex items-center gap-3 hover:bg-white/5">
      <div className="w-9 h-9 rounded-full bg-cyan/15 border border-cyan/30 flex items-center justify-center shrink-0">
        <Calendar size={13} className="text-cyan" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-white text-sm font-medium truncate">
          {b.full_name || "Customer"}
          {b.hours_late != null && <span className="ml-2 label-caps text-[9px] text-red-300">{b.hours_late}h overdue</span>}
        </div>
        <div className="text-white/50 text-[11px] truncate">
          {b.vehicle_name} · {b.status?.replace(/_/g, " ")}
        </div>
        <div className="text-white/40 text-[10px] flex items-center gap-2 mt-0.5">
          {b.customer_phone && <span className="inline-flex items-center gap-0.5"><Phone size={9} />{b.customer_phone}</span>}
          {time && <span>{new Date(time).toLocaleString()}</span>}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-lime-300 font-medium text-sm">${(b.amount || 0).toFixed(0)}</div>
      </div>
    </div>
  );
}

function renderVehicleRow(v) {
  return (
    <div className="p-3 flex items-center gap-3 hover:bg-white/5">
      <div className="w-14 h-10 rounded-lg overflow-hidden bg-black shrink-0">
        {v.image_url ? <img src={v.image_url} alt="" className="w-full h-full object-cover" /> :
          <div className="w-full h-full flex items-center justify-center text-white/30 text-[9px]">no photo</div>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-white text-sm font-medium truncate">{v.name}</div>
        <div className="text-white/50 text-[11px] truncate">
          {v.license_plate || "no plate"} · {v.mileage != null ? `${v.mileage.toLocaleString()} mi` : "—"}
        </div>
        {v.current_customer && <div className="text-cyan text-[11px] truncate">{v.current_customer}</div>}
      </div>
      <div className="label-caps text-[9px] px-2 py-0.5 rounded-full bg-white/10 text-white/70 shrink-0">
        {(v.status || "").replace(/_/g, " ")}
      </div>
    </div>
  );
}

function renderMoneyRow(b) {
  return (
    <div className="p-3 flex items-center gap-3 hover:bg-white/5">
      <div className="w-9 h-9 rounded-full bg-amber-500/20 border border-amber-400/40 flex items-center justify-center shrink-0">
        <DollarSign size={13} className="text-amber-200" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-white text-sm font-medium truncate">{b.full_name}</div>
        <div className="text-white/50 text-[11px] truncate">{b.vehicle_name}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-amber-200 font-medium">${(b.amount || 0).toFixed(0)}</div>
        <div className="label-caps text-[9px] text-white/40">{b.deposit_status || b.payment_status}</div>
      </div>
    </div>
  );
}

function renderFollowUpRow(f) {
  return (
    <div className="p-3 flex items-center gap-3 hover:bg-white/5">
      <div className="w-9 h-9 rounded-full bg-amber-400/20 border border-amber-300/40 flex items-center justify-center shrink-0">
        <Clock size={13} className="text-amber-200" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-white text-sm font-medium truncate">{f.name}</div>
        <div className="text-white/50 text-[11px] truncate">{f.vehicle_name || f.service} · {f.source}</div>
        {f.next_follow_up && (
          <div className="text-amber-200 text-[10px]">Due {new Date(f.next_follow_up).toLocaleString()}</div>
        )}
      </div>
    </div>
  );
}

function Panel({ title, count, accent = "cyan", subtitle, testId, children }) {
  const accents = {
    cyan:  "border-cyan/25",
    amber: "border-amber-400/25",
    red:   "border-red-400/40",
    lime:  "border-lime-400/25",
  };
  return (
    <div data-testid={testId} className={`rounded-2xl border ${accents[accent]} bg-black/25 overflow-hidden`}>
      <div className="p-3 border-b border-white/5 flex items-center justify-between">
        <div>
          <div className="label-caps text-[10px] text-white/70 tracking-widest">{title}</div>
          {subtitle && <div className="text-white/40 text-[10px] mt-0.5">{subtitle}</div>}
        </div>
        {count != null && (
          <span className="label-caps text-[9px] px-2 py-0.5 rounded-full bg-white/10 text-white/70">{count}</span>
        )}
      </div>
      <div className="divide-y divide-white/5">{children}</div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="p-6 text-center text-white/40 text-xs italic">{label}</div>;
}

function BookingRow({ b, kind }) {
  const time = b.pickup_datetime || b.start_at || b.end_at;
  const tone = kind === "late" ? "red" : kind === "return" ? "amber" : "cyan";
  const tones = { cyan: "text-cyan bg-cyan/15", amber: "text-amber-200 bg-amber-500/15", red: "text-red-200 bg-red-500/20" };
  return (
    <div className="p-3 flex items-center gap-3 hover:bg-white/5 transition">
      <div className={`w-10 shrink-0 text-center rounded-lg py-1 ${tones[tone]}`}>
        <div className="text-[9px] label-caps">
          {time ? new Date(time).toLocaleString(undefined, { month: "short", day: "numeric" }) : "—"}
        </div>
        <div className="text-sm font-serif tabular-nums leading-none">
          {time ? new Date(time).toLocaleString(undefined, { hour: "numeric", minute: "2-digit" }) : "—"}
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-white text-sm font-medium truncate">
          {b.full_name}
          {kind === "late" && b.hours_late != null && (
            <span className="ml-2 label-caps text-[9px] text-red-300">· {b.hours_late}h overdue</span>
          )}
        </div>
        <div className="text-white/50 text-[11px] truncate">
          {b.vehicle_name} {b.pickup && <>· {b.pickup}</>}
        </div>
        <div className="text-white/40 text-[10px] flex items-center gap-2 mt-0.5">
          {b.customer_phone && <span className="inline-flex items-center gap-0.5"><Phone size={9} /> {b.customer_phone}</span>}
          <span className="label-caps">{b.status?.replace(/_/g, " ")}</span>
          {b.deposit_status === "pending" && <span className="label-caps text-amber-200">DEP PENDING</span>}
          {!b.contract_signed_at && kind === "pickup" && <span className="label-caps text-red-300">CONTRACT MISSING</span>}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-lime-300 font-medium text-sm">${(b.amount || 0).toFixed(0)}</div>
      </div>
    </div>
  );
}

function QuickActions() {
  const goto = (hash) => { window.location.hash = hash; };
  const actions = [
    { label: "New Booking",     icon: Calendar,      onClick: () => goto("#schedule") },
    { label: "New Lead",        icon: Sparkles,      onClick: () => goto("#crm") },
    { label: "New Customer",    icon: Crown,         onClick: () => goto("#customers") },
    { label: "Collect Payment", icon: Wallet,        onClick: () => goto("#deposits") },
    { label: "Assign Driver",   icon: Car,           onClick: () => goto("#drivers") },
    { label: "Add Vehicle",     icon: Car,           onClick: () => goto("#fleet") },
    { label: "Send SMS",        icon: Bell,          onClick: () => { goto("#customers"); toast.info("Open a customer to send SMS"); } },
    { label: "Create Contract", icon: FileText,      onClick: () => goto("#rentals") },
  ];
  return (
    <div className="rounded-2xl border border-white/10 bg-black/25 p-3">
      <div className="label-caps text-[10px] text-white/60 mb-2 tracking-widest">Quick actions</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-1.5">
        {actions.map((a) => (
          <button
            key={a.label}
            data-testid={`cc-qa-${a.label.toLowerCase().replace(/\s/g, "-")}`}
            onClick={a.onClick}
            className="inline-flex flex-col items-center gap-1 rounded-lg border border-white/10 bg-white/[0.03] p-2 hover:bg-cyan/[0.08] hover:border-cyan/40 transition"
          >
            <a.icon size={13} className="text-cyan" />
            <span className="label-caps text-[9px] text-white/70 tracking-widest">{a.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* -------- Fleet Status Grid -------- */

const STATUS_STYLES = {
  AVAILABLE:            "bg-lime-400/15 text-lime-200 border-lime-400/40",
  RESERVED:             "bg-cyan/15 text-cyan border-cyan/40",
  PICKUP_TODAY:         "bg-cyan/20 text-cyan border-cyan/50",
  CHAUFFEUR_ASSIGNMENT: "bg-purple-400/15 text-purple-200 border-purple-400/40",
  OUT_ON_RENTAL:        "bg-amber-400/15 text-amber-200 border-amber-400/40",
  RETURNING_TODAY:      "bg-amber-500/15 text-amber-200 border-amber-500/40",
  LATE:                 "bg-red-500/20 text-red-200 border-red-500/50",
  MAINTENANCE:          "bg-orange-400/15 text-orange-200 border-orange-400/40",
  DETAILING:            "bg-blue-400/15 text-blue-200 border-blue-400/40",
  DAMAGED:              "bg-red-400/20 text-red-200 border-red-400/50",
  DISABLED:             "bg-white/10 text-white/60 border-white/20",
  DO_NOT_RENT:          "bg-red-500/20 text-red-200 border-red-500/50",
};

function FleetStatusGrid({ fleet }) {
  const [filter, setFilter] = useState("ALL");
  const filtered = filter === "ALL" ? fleet : fleet.filter((v) => v.status === filter);
  const counts = fleet.reduce((acc, v) => { acc[v.status] = (acc[v.status] || 0) + 1; return acc; }, {});

  return (
    <div data-testid="cc-fleet" className="rounded-2xl border border-white/10 bg-black/25 overflow-hidden">
      <div className="p-3 border-b border-white/5 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="label-caps text-[10px] text-cyan tracking-widest">Live fleet status</div>
          <div className="text-white/60 text-xs mt-0.5">{fleet.length} vehicle{fleet.length === 1 ? "" : "s"} · updated live</div>
        </div>
        <div className="flex flex-wrap gap-1">
          {["ALL", "AVAILABLE", "OUT_ON_RENTAL", "PICKUP_TODAY", "LATE", "MAINTENANCE"].map((s) => (
            <button
              key={s}
              data-testid={`cc-fleet-filter-${s.toLowerCase()}`}
              onClick={() => setFilter(s)}
              className={`px-2.5 py-1 rounded-full text-[10px] label-caps tracking-widest transition ${
                filter === s ? "bg-cyan text-black font-bold" : "border border-white/15 text-white/60 hover:text-white"
              }`}
            >
              {s.replace(/_/g, " ")} {s !== "ALL" && counts[s] ? `· ${counts[s]}` : ""}
            </button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 p-3">
        {filtered.map((v) => {
          const style = STATUS_STYLES[v.status] || "bg-white/5 text-white border-white/10";
          return (
            <div key={v.id} data-testid={`cc-fleet-card-${v.id}`}
                 className="rounded-xl border border-white/10 bg-black/30 overflow-hidden">
              <div className="aspect-[16/9] bg-black relative overflow-hidden">
                {v.image_url ? (
                  <img src={v.image_url} alt={v.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">
                    No photo
                  </div>
                )}
                <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9px] label-caps tracking-widest border ${style}`}>
                  {v.status.replace(/_/g, " ")}
                </div>
              </div>
              <div className="p-2.5">
                <div className="text-white text-sm font-medium truncate">
                  {v.name}
                </div>
                <div className="text-white/50 text-[10px] flex items-center gap-2 mt-0.5">
                  {v.license_plate && <span className="tabular-nums">{v.license_plate}</span>}
                  {v.mileage != null && <span>· {v.mileage.toLocaleString()} mi</span>}
                </div>
                {v.current_customer && (
                  <div className="text-cyan text-[11px] mt-1 truncate">
                    {v.current_customer}
                  </div>
                )}
                {v.return_at && (
                  <div className="text-white/40 text-[10px]">
                    {v.status === "LATE" ? "Due back " : "Return "}
                    {new Date(v.return_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-white/40 text-xs py-8 italic">
            No vehicles in this status.
          </div>
        )}
      </div>
    </div>
  );
}

/* -------- helpers -------- */
function deltaPct(current, prev) {
  if (!prev || prev === 0) return null;
  return ((current - prev) / prev) * 100;
}
